import { useEffect } from "react";
import { toast } from "react-toastify";
import {
  addToPot,
  ante,
  bet,
  call,
  cancelHand,
  check,
  dealCards,
  discard,
  endGame,
  endHand,
  fold,
  passCard,
  passDeal,
  playerConnected,
  playerDisconnected,
  playerJoin,
  playerLeft,
  rejoin,
  returnToDeck,
  sessionDeactivated,
  show,
  sitout,
  take,
} from "../slice/gameStateSlice";

/**
 * useGameSignalR - Manages all SignalR .on() event subscriptions for the game.
 * 
 * US-007-S1A-EXTRACT-SIGNALR-HANDLERS-001
 * 
 * This hook subscribes to SignalR events on mount and cleans up on unmount.
 * It does NOT handle .invoke() calls - those remain in Main.js.
 * 
 * @param {Object} params - Hook parameters
 * @param {Object} params.connection - SignalR hub connection object
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {Object} params.stateRef - React ref containing current game state
 * @param {Object} params.meetingAPI - Video meeting API (join/leave)
 * @param {Function} params.navigator - React Router navigate function
 * @param {boolean} params.isCreator - Whether current user is game creator
 * @param {Function} params.setKickedModalOpen - Setter for kicked modal visibility
 * @param {Function} params.setSettlementModalEndGameOpen - Setter for settlement modal visibility
 */
export function useGameSignalR({
  connection,
  dispatch,
  stateRef,
  meetingAPI,
  navigator,
  isCreator,
  setKickedModalOpen,
  setSettlementModalEndGameOpen,
}) {
  useEffect(() => {
    // Guard: only subscribe when connection is ready
    if (!connection?.connectionId) return;

    console.log("[useGameSignalR] Subscribing to SignalR events, connectionId:", connection.connectionId);

    // ===== Meeting Events =====
    connection.on("Join_Meeting", () => {
      console.log("[LOG][SignalR] Event: Join_Meeting");
      meetingAPI?.join?.();
    });

    connection.on("Leave_Meeting", () => {
      console.log("[LOG][SignalR] Event: Leave_Meeting");
      meetingAPI?.leave?.();
    });

    if (!isCreator) {
      connection.on("ReceiveAlertNotifictionVideo", (message) => {
        console.log("[LOG][SignalR] Event: ReceiveAlertNotifictionVideo", { message });
        toast(message);
      });
    }

    // ===== Connection Events =====
    connection.on("Other_Connected", (UserId, ConnectionId) => {
      console.log("[LOG][SignalR] Event: Other_Connected", { UserId, ConnectionId });
      dispatch(
        playerConnected({
          ConnectionId,
          UserId,
        })
      );
    });

    // DC-MULTITAB: Handle session takeover (another tab opened for same player/game)
    connection.on("Session_TakenOver", (payload) => {
      console.log("[DC-MULTITAB] Session_TakenOver", payload);
      dispatch(sessionDeactivated());
      toast.info("This game is now active in another tab. This window is no longer connected.", {
        autoClose: false,
        closeOnClick: false,
      });
    });

    connection.on("Other_Disconnected", (UserId, ConnectionId) => {
      console.log("[LOG][SignalR] Event: Other_Disconnected", { UserId, ConnectionId });
      alert("Your Session Is Closed Due To Joining the Game In Other Browser");
      navigator("/");
    });

    connection.on("Other_Joined", (userId, playerImage, userName, connectionId) => {
      console.log("[LOG][SignalR] Event: Other_Joined", { userId, userName, connectionId });
      dispatch(
        playerJoin({
          userId,
          playerImage,
          userName,
          connectionId,
        })
      );
    });

    // ===== Card Events =====
    connection.on("PassCard", (draggingCards, currentIndex, type) => {
      console.log("[LOG][SignalR] Event: PassCard", { cardCount: draggingCards?.length, currentIndex, type });
      dispatch(
        passCard({
          draggingCards: draggingCards.map((x) => ({
            Index: x.index,
            Type: x.type,
            Value: x.value,
            Presentation: x.presentation,
            CommunityIndex: x.communityIndex,
          })),
          Index: currentIndex,
          Type: type,
        })
      );
    });

    connection.on("DealCards", (DealCards, action) => {
      console.log("[LOG][SignalR] Event: DealCards", { cardCount: DealCards?.length, action });
      dispatch(
        dealCards({
          dealCards: DealCards.map((x) => ({
            Index: x.index,
            Type: x.type,
            Value: x.value,
            Presentation: x.presentation,
            CommunityIndex: x.communityIndex,
          })),
          LastActionPerformed: action,
        })
      );
    });

    connection.on("Discard", (draggingCards, index) => {
      console.log("[LOG][SignalR] Event: Discard", { cardCount: draggingCards?.length, index });
      dispatch(
        discard({
          selectedCards: draggingCards.map((x) => ({
            Index: x.index,
            Type: x.type,
            Value: x.value,
            Presentation: x.presentation,
            CommunityIndex: x.communityIndex,
          })),
          Index: index,
        })
      );
    });

    connection.on("ReturnToDeck", (draggingCards, index) => {
      console.log("[LOG][SignalR] Event: ReturnToDeck", { cardCount: draggingCards?.length, index });
      dispatch(
        returnToDeck({
          selectedCards: draggingCards.map((x) => ({
            Index: x.index,
            Type: x.type,
            Value: x.value,
            Presentation: x.presentation,
            CommunityIndex: x.communityIndex,
          })),
          Index: index,
        })
      );
    });

    connection.on("Show", (draggingCards, index) => {
      console.log("[LOG][SignalR] Event: Show", { cardCount: draggingCards?.length, index });
      dispatch(
        show({
          selectedCards: draggingCards.map((x) => ({
            Index: x.index,
            Type: x.type,
            Value: x.value,
            Presentation: x.presentation,
            CommunityIndex: x.communityIndex,
          })),
          Index: index,
        })
      );
    });

    // ===== Player Events =====
    connection.on("Player_Left", (index, leftId, currentId, dealerId, gameCreatorId) => {
      console.log("[LOG][SignalR] Event: Player_Left", { index, leftId, currentId, dealerId });
      // DC-FE-PLAYERLEFT-TOAST-AND-KICKED-MODAL-001:
      // Show toast for remaining players when someone leaves.
      // Look up player name before dispatch removes them from ActivePlayers.
      const leftPlayer = stateRef.current.gameHash?.ActivePlayers?.find(
        (p) => p.PlayerId === leftId
      );
      const leftPlayerName = leftPlayer?.PlayerName || "A player";
      toast.info(`${leftPlayerName} left the game`);

      // DC-FE-PLAYERLEFT-CONSUME-PAYLOAD-001:
      // Use backend-sent turn/dealer/host IDs as source of truth.
      dispatch(
        playerLeft({
          Index: index,
          LeftId: leftId,
          CurrentId: currentId,
          DealerId: dealerId,
          GameCreatorId: gameCreatorId,
        })
      );
    });

    connection.on("Kicked_Out", () => {
      console.log("[LOG][SignalR] Event: Kicked_Out");
      // DC-FE-PLAYERLEFT-TOAST-AND-KICKED-MODAL-001:
      // Show styled DcDialog modal instead of browser alert.
      // Navigation happens on modal confirm.
      setKickedModalOpen(true);
    });

    connection.on("Player_Disconnected", (index, playerName) => {
      console.log("[LOG][SignalR] Event: Player_Disconnected", { index, playerName });
      toast.info(`${playerName || "A player"} disconnected`);
      dispatch(playerDisconnected(index));
    });

    // ===== Betting Events =====
    // US-002-S2C2: Accept extended payload with betting state fields
    connection.on("Bet", (index, amount, currentId, betStatus, betStatusIndex, CurrentBet, CurrentRoundStatusArray) => {
      console.log("[LOG][SignalR] Event: Bet", { index, amount, currentId, betStatus });
      dispatch(
        bet({
          Amount: amount,
          Index: index,
          CurrentId: currentId,
          BetStatus: betStatus,
          BetStatusIndex: betStatusIndex,
          CurrentBet,
          CurrentRoundStatusArray,
        })
      );
    });

    connection.on("Take", (index, amount) => {
      console.log("[LOG][SignalR] Event: Take", { index, amount });
      dispatch(
        take({
          Index: index,
          Amount: amount,
        })
      );
    });

    // US-002-S2A2: Accept extended payload with betting state fields
    connection.on("Call", (index, currentId, betStatus, betStatusIndex, CurrentBet, CurrentRoundStatusArray) => {
      console.log("[LOG][SignalR] Event: Call", { index, currentId, betStatus });
      dispatch(
        call({
          Index: index,
          CurrentId: currentId,
          BetStatus: betStatus,
          BetStatusIndex: betStatusIndex,
          CurrentBet,
          CurrentRoundStatusArray,
        })
      );
    });

    connection.on("AddToPot", (index, amount) => {
      console.log("[LOG][SignalR] Event: AddToPot", { index, amount });
      dispatch(
        addToPot({
          Index: index,
          Amount: amount,
        })
      );
    });

    connection.on("Ante", (index, amount) => {
      console.log("[LOG][SignalR] Event: Ante", { index, amount });
      dispatch(
        ante({
          Index: index,
          Amount: amount,
        })
      );
    });

    // US-002-S2B2: Accept extended payload with betting state fields
    connection.on("Check", (index, currentId, betStatus, betStatusIndex, CurrentBet, CurrentRoundStatusArray) => {
      console.log("[LOG][SignalR] Event: Check", { index, currentId, betStatus });
      dispatch(
        check({
          Index: index,
          CurrentId: currentId,
          BetStatus: betStatus,
          BetStatusIndex: betStatusIndex,
          CurrentBet,
          CurrentRoundStatusArray,
        })
      );
    });

    // US-002-S2D2: Extended with CurrentBet + CurrentRoundStatusArray from backend
    connection.on("Fold", (index, currentId, betStatus, betStatusIndex, CurrentBet, CurrentRoundStatusArray) => {
      console.log("[LOG][SignalR] Event: Fold", { index, currentId, betStatus });
      dispatch(
        fold({
          Index: index,
          CurrentId: currentId,
          BetStatus: betStatus,
          BetStatusIndex: betStatusIndex,
          CurrentBet,
          CurrentRoundStatusArray,
        })
      );
    });

    // ===== Game Flow Events =====
    connection.on("Cancel_Hand", () => {
      console.log("[LOG][SignalR] Event: Cancel_Hand");
      dispatch(cancelHand());
    });

    connection.on("End_Hand", (index) => {
      console.log("[LOG][SignalR] Event: End_Hand", { index });
      dispatch(endHand(index));
    });

    connection.on("Sitout", (index, currentId, betStatus, betStatusIndex) => {
      console.log("[LOG][SignalR] Event: Sitout", { index, currentId, betStatus });
      dispatch(
        sitout({
          Index: index,
          CurrentId: currentId,
          BetStatus: betStatus,
          BetStatusIndex: betStatusIndex,
        })
      );
    });

    connection.on("Rejoin", (index) => {
      console.log("[LOG][SignalR] Event: Rejoin", { index });
      dispatch(rejoin(index));
    });

    connection.on("Endgame", (index) => {
      console.log("[LOG][SignalR] Event: Endgame", { index });
      dispatch(endGame(index));
      setSettlementModalEndGameOpen(true);
    });

    connection.on("PassDeal", (dealerId) => {
      console.log("[LOG][SignalR] Event: PassDeal", { dealerId });
      dispatch(passDeal(dealerId));
    });

    // ===== Cleanup on unmount =====
    return () => {
      console.log("[useGameSignalR] Unsubscribing from SignalR events");
      connection.off("Join_Meeting");
      connection.off("Leave_Meeting");
      connection.off("ReceiveAlertNotifictionVideo");
      connection.off("Other_Connected");
      connection.off("Session_TakenOver");
      connection.off("Other_Disconnected");
      connection.off("Other_Joined");
      connection.off("PassCard");
      connection.off("DealCards");
      connection.off("Discard");
      connection.off("ReturnToDeck");
      connection.off("Show");
      connection.off("Player_Left");
      connection.off("Kicked_Out");
      connection.off("Player_Disconnected");
      connection.off("Bet");
      connection.off("Take");
      connection.off("Call");
      connection.off("AddToPot");
      connection.off("Ante");
      connection.off("Check");
      connection.off("Fold");
      connection.off("Cancel_Hand");
      connection.off("End_Hand");
      connection.off("Sitout");
      connection.off("Rejoin");
      connection.off("Endgame");
      connection.off("PassDeal");
    };
    // RATIONALE: depending on connection object causes unsubscribe/resubscribe churn and missed Other_Joined events (see 2026-01-08 SignalR regression report)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    connection?.connectionId,
    dispatch,
    stateRef,
    meetingAPI,
    navigator,
    isCreator,
    setKickedModalOpen,
    setSettlementModalEndGameOpen,
  ]);
}
